import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { OrganizationFilter } from '../../components/OrganizationFilter';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { executionTriggerLabel } from '../../components/ui/TriggerTypeBadge';
import { canSeeAiCost, formatEur } from '../../lib/ai-cost';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

function apiErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string } } } | null)?.response?.data;
  return data?.message ?? 'Action impossible. Réessayez dans un instant.';
}

interface ExecutionRow {
  id: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PARTIAL';
  trigger: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  /** Présent uniquement pour le super-administrateur. */
  aiCostEur?: string | null;
  externalExecutionId: string;
  automation: { id: string; name: string; slug: string };
  organization?: { id: string; name: string };
  triggeredByUser: { fullName: string } | null;
}

interface ListResponse {
  data: ExecutionRow[];
  meta: { page: number; pageSize: number; total: number };
}

async function fetchExecutions(filters: Filters): Promise<ListResponse> {
  const res = await apiClient.get<ListResponse>('/executions', {
    params: { page: 1, pageSize: 50, ...filters },
  });
  return res.data;
}

interface Filters {
  status?: string;
  trigger?: string;
  organizationId?: string;
}

export function ExecutionsListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canDelete = user?.role === 'SUPER_ADMIN';
  const canManage =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'ADWEB_OPERATOR' ||
    user?.role === 'CLIENT_ADMIN';
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const showAiCost = canSeeAiCost(user?.role);
  const qc = useQueryClient();
  const [filters, setFilters] = useState<Filters>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['executions', filters],
    queryFn: () => fetchExecutions(filters),
    placeholderData: keepPreviousData,
    refetchInterval: 5_000,
  });

  const del = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/executions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['executions'] }),
  });

  const retry = useMutation({
    mutationFn: (id: string) => apiClient.post(`/executions/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['executions'] }),
    onError: (err) => setActionError(apiErrorMessage(err)),
  });

  const exportCsv = useMutation({
    mutationFn: async () => {
      const res = await apiClient.get('/executions/export', {
        params: filters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `executions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err) => setActionError(apiErrorMessage(err)),
  });

  const exportPdf = useMutation({
    mutationFn: async () => {
      const res = await apiClient.get('/executions/export/pdf', {
        params: filters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `executions-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err) => setActionError(apiErrorMessage(err)),
  });

  function handleDelete(id: string) {
    if (window.confirm('Supprimer définitivement cette exécution ? Action irréversible.')) {
      del.mutate(id);
    }
  }

  return (
    <>
      <PageHeader
        helpTopic="executions"
        title={t('executions.title')}
        subtitle={t('executions.subtitle')}
        actions={
          canManage ? (
            <>
              <Button
                variant="inverse"
                size="sm"
                onClick={() => {
                  setActionError(null);
                  exportPdf.mutate();
                }}
                disabled={exportPdf.isPending}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {exportPdf.isPending ? 'Export…' : 'Exporter PDF'}
              </Button>
              <Button
                variant="inverse"
                size="sm"
                onClick={() => {
                  setActionError(null);
                  exportCsv.mutate();
                }}
                disabled={exportCsv.isPending}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {exportCsv.isPending ? 'Export…' : 'Exporter CSV'}
              </Button>
            </>
          ) : null
        }
      />
      <section className="px-4 py-6 sm:px-6 lg:px-8">
        <div
          className="mb-4 flex flex-wrap items-center gap-2"
          role="group"
          aria-label={t('app.filter')}
        >
          {isPlatform && (
            <OrganizationFilter
              value={filters.organizationId ?? ''}
              onChange={(id) => setFilters((f) => ({ ...f, organizationId: id || undefined }))}
            />
          )}
          {(['', 'RUNNING', 'SUCCEEDED', 'FAILED', 'PARTIAL'] as const).map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setFilters((f) => ({ ...f, status: s || undefined }))}
              aria-pressed={(filters.status ?? '') === s}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
                (filters.status ?? '') === s
                  ? 'bg-brand text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {s ? t(`status.${s}`) : t('app.all')}
            </button>
          ))}
        </div>

        {actionError && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {actionError}
          </div>
        )}

        <div className="overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {t('executions.automation')}
                </th>
                {isPlatform && (
                  <th scope="col" className="hidden px-4 py-3 lg:table-cell">
                    {t('app.organization')}
                  </th>
                )}
                <th scope="col" className="px-4 py-3">
                  {t('executions.status')}
                </th>
                <th scope="col" className="hidden px-4 py-3 md:table-cell">
                  {t('executions.trigger')}
                </th>
                <th scope="col" className="hidden px-4 py-3 sm:table-cell">
                  {t('executions.startedAt')}
                </th>
                <th scope="col" className="hidden px-4 py-3 lg:table-cell">
                  {t('executions.duration')}
                </th>
                {showAiCost && (
                  <th scope="col" className="hidden px-4 py-3 md:table-cell">
                    Coût IA
                  </th>
                )}
                <th scope="col" className="px-4 py-3" aria-label={t('app.actions')}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(list.data?.data ?? []).map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{e.automation.name}</div>
                    <div className="text-xs text-slate-400">{e.externalExecutionId}</div>
                    {/* Téléphone : la date remplace la colonne « Démarrée ». */}
                    <div className="text-xs text-slate-500 sm:hidden">
                      {new Date(e.startedAt).toLocaleString('fr-FR')}
                    </div>
                  </td>
                  {isPlatform && (
                    <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">
                      {e.organization?.name ?? '—'}
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="hidden px-4 py-2.5 text-slate-600 md:table-cell">
                    {executionTriggerLabel(e.trigger)}
                  </td>
                  <td className="hidden px-4 py-2.5 text-slate-500 sm:table-cell">
                    {new Date(e.startedAt).toLocaleString('fr-FR')}
                  </td>
                  <td className="hidden px-4 py-2.5 text-slate-500 lg:table-cell">
                    {e.durationMs != null ? `${(e.durationMs / 1000).toFixed(1)} s` : '—'}
                  </td>
                  {showAiCost && (
                    <td className="hidden px-4 py-2.5 tabular-nums text-slate-500 md:table-cell">
                      {formatEur(e.aiCostEur, 4)}
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/executions/${e.id}`}
                        className="text-xs font-medium text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                      >
                        {t('app.details')} →
                      </Link>
                      {canManage && e.status === 'FAILED' && (
                        <button
                          type="button"
                          onClick={() => {
                            setActionError(null);
                            retry.mutate(e.id);
                          }}
                          disabled={retry.isPending}
                          className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
                        >
                          Relancer
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(e.id)}
                          disabled={del.isPending}
                          className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {list.data && list.data.data.length === 0 && (
                <tr>
                  <td
                    colSpan={(isPlatform ? 7 : 6) + (showAiCost ? 1 : 0)}
                    className="px-4 py-8 text-center text-slate-400"
                  >
                    {t('executions.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-xs text-slate-400">
          {t('executions.refreshHint', { count: list.data?.meta.total ?? 0 })}
        </p>
      </section>
    </>
  );
}
